import React from 'react';
import { useDispatch } from 'react-redux';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import ErrorIcon from 'apps/web_forms/assets/errorgraphic.svg';
import CloseIcon from 'apps/web_forms/assets/close.svg';
import SuccessIcon from 'apps/web_forms/assets/formicon.svg';
import RenderButtons from 'apps/web_forms/components/commons/RenderButtons';
import DMButton from './DMButton';
import { COLORS } from 'apps/web_forms/utils/constants';
import { setCurrentSelectedMultimediaInput } from 'apps/web_forms/slices/multimediaSlice';

const StyledModal = styled.div`
  font-size: 16px; /* make sure ems within the component are sized the same no matter where it is included */
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100%;
  z-index: 9999;
  position: fixed;
  background-color: rgba(0, 0, 0, 0.3);
  // ${(props) => !props.isFullScreen && 'background-color: rgba(0, 0, 0, 0.5);'}
  visibility: ${(props) => (props.open ? 'visible' : 'hidden')};
  opacity: ${(props) => (props.open ? 1 : 0)};
  transition: opacity linear 0.25s;
  left: 0;
  top: 0;

  .component-modal-box {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    position: fixed;
    top: ${(props) => (props.isFullScreen ? '0px' : '16%')};
    left: ${(props) => (props.isFullScreen ? '0px' : '13%')};
    height: ${(props) => (props.isFullScreen ? '100vh' : '82%')};
    width: ${(props) => (props.isFullScreen ? '100vw' : '83%')};
    ${(props) =>
      !props.isFullScreen ? 'min-width: 37em;' : ''}
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    background-color: white;
    ${(props) => props.isFullScreen && 'position: absolute;'}

    .close-button {
      position: ${(props) => (props.isFullScreen ? 'absolute' : 'relative')};
      text-align: right;
      width: 100%;
      margin-right: 0.5em;
      font-size: 2.5em;
      cursor: pointer;
      opacity: 0.4;
      ${(props) => props.isFullScreen && 'top: 0;'}
      ${(props) => props.isFullScreen && 'margin-top: 0;'}

      .close-icon {
        height: 0.5em;
        width: 0.5em;
      }
    }

    .component-modal-icon {
      width: 100%;
      height: 15em;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .main-component-modal-text {
      padding: 0px 0.9em;
      border-bottom: 1px solid ${COLORS.lightgrey};
      display: inline-block;
      align-items: center;
      justify-content: center;
      width: 100%;
      min-height: 1.4em;
      text-align: center;
      font-weight: bold;
      overflow: hidden;
      text-overflow: ellipsis;
      font-size: ${(props) => (props.hasIcon ? '1.3em' : '2em')};
    }

    .elements-container {
      width: 59% !important;
      padding-right: 1em;
    }
    
    .help-tip,
    .final-message {
      width: 100%;
      height: 2em;
      text-align: center;
      padding: 4em;
    }
  }
`;

const Buttons = styled.div`
  width: 100%;
  height: 5em;
  display: flex;
  justify-content: space-evenly;
  align-items: center;
`;

// TODO: need some more aesthetic adjustments in here
const ComponentModal = ({
  mainMessage,
  helpTip,
  isOpen,
  setIsOpen,
  onClickDone,
  icon,
  children,
  buttons,
  onClose,
  testId,
  isFullScreen,
  isFinal,
}) => {
  const dispatch = useDispatch();

  const closeModal = () => {
    if (onClose && typeof onClose === 'function') {
      onClose();
    }
    if(onClickDone && typeof onClickDone === 'function') {
      onClickDone();
    }
    dispatch(setCurrentSelectedMultimediaInput({}));
    setIsOpen(false);
  };

  const hasChildren = children != null;
  const hasIcon = icon != null;

  return (
    <StyledModal
      open={isOpen}
      standard={!hasChildren}
      hasIcon={hasIcon}
      isFullScreen={isFullScreen}
      role='dialog'
    >
      <div className='component-modal-box' data-testid={testId}>
        {!isFinal && (
          <span
            className='close-button'
            onClick={closeModal}
            role='button'
            aria-hidden='true'
            data-testid='modal-close-button'
          >
            <CloseIcon className='close-icon' />
          </span>
        )}
        {icon && (
          <div className='component-modal-icon'>
            {icon === 'success' ? <SuccessIcon /> : <ErrorIcon />}
          </div>
        )}
        <div className='main-component-modal-text'>{mainMessage}</div>
        {helpTip && <div className='help-tip'>{helpTip}</div>}
        {children}
        {isFinal ? (
          <div className='final-message'>You may close the tab</div>
        ) : (
          <Buttons>
            {buttons == null ? (
              <DMButton filled callbackMethod={closeModal}>
                Done
              </DMButton>
            ) : (
              <RenderButtons buttons={buttons} />
            )}
          </Buttons>
        )}
      </div>
    </StyledModal>
  );
};

ComponentModal.propTypes = {
  mainMessage: PropTypes.string,
  helpTip: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  icon: PropTypes.string,
  children: PropTypes.node,
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      filled: PropTypes.bool,
      materialText: PropTypes.bool,
      disabled: PropTypes.bool,
    })
  ),
  onClose: PropTypes.func,
  testId: PropTypes.string,
  isFullScreen: PropTypes.bool,
  isFinal: PropTypes.bool,
};

ComponentModal.defaultProps = {
  mainMessage: '',
  helpTip: '',
  icon: undefined,
  children: undefined,
  buttons: undefined,
  onClose: () => {},
  testId: 'modal-box',
  isFullScreen: false,
  isFinal: false,
};

export default ComponentModal;
